import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Avatar,
  Box,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FormikValues, useFormikContext } from 'formik';
import { Page } from '@juno/client-api/model';
import { usePageSearch } from '@juno/utils';

/**
 * Base props for the PageSelectorInput component.
 * @template T - Type of the Formik form values
 */
interface PageSelectorInputBaseProps<T extends FormikValues> {
  /**
   * Name of the Formik field that stores the selected page id (e.g., 'contact_page').
   * Used to read and update the selected internal page value.
   */
  fieldId: keyof T;
  /**
   * (Optional) Name of the boolean Formik field that indicates if the selection is internal (true) or external (false).
   * Only required if switching between internal page and external URL is allowed.
   */
  fieldInternal?: keyof T;
  /**
   * Label text to display in the component (e.g., 'Contact Page').
   */
  label: string;
  /**
   * Optional helper text to display below the label.
   */
  helperText?: string;
}

/**
 * Props for PageSelectorInput when external URL selection is allowed.
 * @template T - Type of the Formik form values
 */
interface PageSelectorInputWithUrl<T extends FormikValues> extends PageSelectorInputBaseProps<T> {
  /**
   * If true, allows switching between internal page selection and external URL input.
   */
  allowExternalUrl: true;
  /**
   * Name of the Formik field that stores the external URL value.
   * Required when allowExternalUrl is true.
   */
  fieldUrl: keyof T;
}

/**
 * Props for PageSelectorInput when only internal page selection is allowed.
 * @template T - Type of the Formik form values
 */
interface PageSelectorInputWithoutUrl<T extends FormikValues>
  extends PageSelectorInputBaseProps<T> {
  /**
   * If false or omitted, only internal page selection is allowed (no external URL).
   */
  allowExternalUrl?: false;
  /**
   * Should never be provided when allowExternalUrl is false.
   */
  fieldUrl?: never;
}

type PageSelectorInputProps<T extends FormikValues> =
  | PageSelectorInputWithUrl<T>
  | PageSelectorInputWithoutUrl<T>;

function PageSelectorInput<T extends FormikValues>(props: PageSelectorInputProps<T>) {
  const { fieldId, fieldInternal, label, helperText } = props as PageSelectorInputBaseProps<T>;

  const fieldUrl = (props as PageSelectorInputWithUrl<T>).fieldUrl;
  const allowExternalUrl = (props as PageSelectorInputWithUrl<T>).allowExternalUrl;
  const formik = useFormikContext<T>();

  const isExternalAllowed = !!allowExternalUrl;
  const isInternal =
    !isExternalAllowed || !fieldInternal ? true : Boolean(formik.values[fieldInternal as string]);

  const { page, setPage, search, setSearch, searchData, isFetching } = usePageSearch(
    formik.values[fieldId] as string | undefined,
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant='subtitle1'>{label}</Typography>
        {helperText && <Typography variant='body2'>{helperText}</Typography>}
      </Grid>
      <Grid item xs={6} textAlign='right'>
        <Stack direction='row' width='100%' alignItems='flex-start'>
          <Stack sx={{ flex: 1, flexGrow: 1 }}>
            {isExternalAllowed && fieldInternal && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isInternal}
                    onChange={(e) =>
                      formik.setFieldValue(fieldInternal as string, e.target.checked)
                    }
                  />
                }
                label='Internal'
                sx={{ width: 160, mb: 1 }}
              />
            )}

            {isInternal && page && (
              <Card
                sx={{
                  p: 2,
                  boxShadow: 1,
                  background: 'rgba(0,0,0,.03)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Stack>
                  <Typography variant='subtitle1'>{page.title || 'No title'}</Typography>
                </Stack>
                <IconButton
                  size='small'
                  aria-label='Close'
                  onClick={() => {
                    setPage(undefined);
                    formik.setFieldValue(fieldId as string, '');
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Card>
            )}

            {isInternal && !page && (
              <Autocomplete
                options={searchData || []}
                getOptionLabel={(option) => option?.title || ''}
                size='small'
                loading={isFetching}
                value={null}
                noOptionsText={search ? 'No results found' : 'Search to find options'}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Avatar src={option.icon} variant='rounded' sx={{ mr: 2 }} />
                    <Box>
                      <Typography>{option.title}</Typography>
                      <Typography variant='subtitle2'>{option.type}</Typography>
                    </Box>
                  </li>
                )}
                onChange={(_, newValue) => {
                  formik.setFieldValue(fieldId as string, newValue?.id || '');
                  setPage(newValue as unknown as Page | undefined);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Search Pages'
                    onChange={(e) => setSearch(e.target.value)}
                    error={!!formik.errors[fieldId as string]}
                    helperText={formik.errors[fieldId as string] as string}
                  />
                )}
              />
            )}

            {isExternalAllowed && !isInternal && fieldUrl && (
              <TextField
                value={formik.values[fieldUrl] || ''}
                size='small'
                label={`${label} URL`}
                onChange={(e) => formik.setFieldValue(fieldUrl as string, e.target.value)}
                error={!!formik.errors[fieldUrl]}
                helperText={formik.errors[fieldUrl] as string}
              />
            )}
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default PageSelectorInput;
